import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { colors } from '../../../../styles/tokens';
import { ChatService } from '../../../../services/ChatService';
import { IChatMessage, IChatThreadSummary } from '../../../../domains/Chat';
import { useAuth } from '../../../../hooks/auth';
import {
  playNotificationSound,
  unlockNotificationSound,
} from '../../../../utils/playNotificationSound';

import {
  AssignedBadge,
  CloseThreadButton,
  ClosedNotice,
  Composer,
  ComposerInput,
  Container,
  EmptyMessagePane,
  EmptyThreadList,
  Header,
  Layout,
  MessageBubble,
  MessagePane,
  MessagePaneHeader,
  MessageTime,
  MessagesScroll,
  PageTitle,
  SenderName,
  SendButton,
  SoundToggle,
  TabButton,
  Tabs,
  ThreadItem,
  ThreadItemHeader,
  ThreadItemName,
  ThreadItemPreview,
  ThreadListPane,
  UnreadDot,
} from './styles';

const POLL_INTERVAL_MS = 15000;
const SOUND_ENABLED_STORAGE_KEY = 'chat:soundEnabled';

type ChatTab = 'pending' | 'in_progress' | 'closed';

const TABS: { key: ChatTab; label: string }[] = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'in_progress', label: 'Em atendimento' },
  { key: 'closed', label: 'Finalizadas' },
];

const ChatInbox: React.FC = () => {
  const chatService = new ChatService();
  const { user } = useAuth();

  const [threads, setThreads] = useState<IChatThreadSummary[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string>();
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [activeTab, setActiveTab] = useState<ChatTab>('pending');
  const [soundEnabled, setSoundEnabled] = useState(
    () => sessionStorage.getItem(SOUND_ENABLED_STORAGE_KEY) !== 'false',
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Guardam o estado do último poll só pra detectar o que é novo -- não
  // disparam re-render, por isso são refs e não state.
  const previousUnreadTotalRef = useRef<number>();
  const previousLastMessageIdRef = useRef<string>();
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // Só escolhe a aba inicial uma vez, no primeiro load -- depois disso o
  // usuário controla a aba, mesmo que ela fique vazia (ex: encerrou a
  // última pendente, não deve pular sozinho pra outra aba).
  const hasSetInitialTabRef = useRef(false);

  const handleToggleSound = useCallback(() => {
    setSoundEnabled(current => {
      const next = !current;
      sessionStorage.setItem(SOUND_ENABLED_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const loadThreads = useCallback(() => {
    chatService
      .getGymThreads()
      .then(response => {
        setThreads(response.data);

        const unreadTotal = response.data.reduce(
          (total, thread) => total + thread.unread_count,
          0,
        );

        // `undefined` no primeiro load -- não toca som só por ter
        // conversas com mensagem não lida de antes de abrir a página.
        if (
          previousUnreadTotalRef.current !== undefined &&
          unreadTotal > previousUnreadTotalRef.current &&
          soundEnabledRef.current
        ) {
          playNotificationSound();
        }

        previousUnreadTotalRef.current = unreadTotal;

        if (!hasSetInitialTabRef.current) {
          hasSetInitialTabRef.current = true;

          const hasPending = response.data.some(
            thread => thread.status === 'pending',
          );

          if (!hasPending) {
            const firstNonEmptyTab = TABS.find(tab =>
              response.data.some(thread => thread.status === tab.key),
            );

            if (firstNonEmptyTab) setActiveTab(firstNonEmptyTab.key);
          }
        }

        // Se a conversa aberta saiu da aba atual (mudou de status --
        // respondeu e virou "em atendimento", foi encerrada, etc.),
        // fecha o painel: continuar mostrando ela aqui seria inconsistente
        // com a aba selecionada.
        setSelectedStudentId(current => {
          if (!current) return current;

          const thread = response.data.find(t => t.student_user_id === current);

          return thread && thread.status === activeTabRef.current
            ? current
            : undefined;
        });
      })
      .catch(err => console.log(err?.response?.status));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMessages = useCallback(
    (studentId: string) => {
      chatService
        .getGymThreadMessages(studentId)
        .then(response => {
          const { messages: nextMessages } = response.data;
          const lastMessage = nextMessages[nextMessages.length - 1];

          // Conversa aberta já zera o unread ao carregar (backend marca
          // como lida), então a checagem de `loadThreads` não pega
          // mensagem nova chegando aqui dentro -- compara pelo id da
          // última mensagem em vez disso.
          if (
            lastMessage &&
            lastMessage.id !== previousLastMessageIdRef.current &&
            previousLastMessageIdRef.current !== undefined &&
            lastMessage.sender_user_id !== user?.id &&
            soundEnabledRef.current
          ) {
            playNotificationSound();
          }

          previousLastMessageIdRef.current = lastMessage?.id;

          setMessages(nextMessages);
          setThreads(current =>
            current.map(thread =>
              thread.student_user_id === studentId
                ? { ...thread, unread_count: 0 }
                : thread,
            ),
          );
        })
        .catch(err => console.log(err?.response?.status));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id],
  );

  useEffect(() => {
    loadThreads();

    const timer = setInterval(loadThreads, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loadThreads]);

  useEffect(() => {
    previousLastMessageIdRef.current = undefined;

    if (!selectedStudentId) return undefined;

    loadMessages(selectedStudentId);

    const timer = setInterval(
      () => loadMessages(selectedStudentId),
      POLL_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [selectedStudentId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectThread = useCallback((studentId: string) => {
    setSelectedStudentId(studentId);
  }, []);

  const handleSelectTab = useCallback((tab: ChatTab) => {
    setActiveTab(tab);
    // Trocar de aba sempre fecha a conversa aberta -- ela pode nem
    // pertencer à aba nova.
    setSelectedStudentId(undefined);
  }, []);

  const selectedThread = threads.find(
    thread => thread.student_user_id === selectedStudentId,
  );

  const handleSend = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (
        !selectedStudentId ||
        !draft.trim() ||
        sending ||
        selectedThread?.status === 'closed'
      )
        return;

      setSending(true);

      try {
        await chatService.sendGymMessage(selectedStudentId, draft.trim());
        setDraft('');
        loadMessages(selectedStudentId);
        loadThreads();
      } catch (err) {
        console.log(err);
      } finally {
        setSending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStudentId, draft, sending],
  );

  const handleCloseThread = useCallback(async () => {
    if (!selectedStudentId || closing) return;

    setClosing(true);

    try {
      await chatService.closeThread(selectedStudentId);
      loadThreads();
    } catch (err) {
      console.log(err);
    } finally {
      setClosing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentId, closing]);

  const visibleThreads = threads.filter(thread => thread.status === activeTab);

  return (
    <Painel>
      {/* Qualquer clique na página destrava o áudio pro navegador -- sem
          isso, o som nunca toca de verdade (política de autoplay:
          precisa de um gesto do usuário antes do primeiro som). */}
      <Container onClick={unlockNotificationSound}>
        <Header>
          <PageTitle>Atendimento</PageTitle>

          <SoundToggle
            type="button"
            active={soundEnabled}
            onClick={handleToggleSound}
            title={
              soundEnabled
                ? 'Som de notificação ligado'
                : 'Som de notificação desligado'
            }
          >
            <Icon
              name={soundEnabled ? 'volume_up' : 'volume_off'}
              size={20}
              color={soundEnabled ? colors.primary : colors.onSurfaceVariant}
            />
            {soundEnabled ? 'Som ligado' : 'Som desligado'}
          </SoundToggle>
        </Header>

        <Tabs>
          {TABS.map(tab => (
            <TabButton
              key={tab.key}
              type="button"
              active={activeTab === tab.key}
              onClick={() => handleSelectTab(tab.key)}
            >
              {tab.label} (
              {threads.filter(thread => thread.status === tab.key).length})
            </TabButton>
          ))}
        </Tabs>

        <Layout>
          <ThreadListPane>
            {visibleThreads.length === 0 && (
              <EmptyThreadList>Nenhuma conversa nessa aba.</EmptyThreadList>
            )}

            {visibleThreads.map(thread => (
              <ThreadItem
                key={thread.thread_id}
                type="button"
                active={thread.student_user_id === selectedStudentId}
                onClick={() => handleSelectThread(thread.student_user_id)}
              >
                <ThreadItemHeader>
                  <ThreadItemName>{thread.student_fullname}</ThreadItemName>
                  {thread.unread_count > 0 && (
                    <UnreadDot>{thread.unread_count}</UnreadDot>
                  )}
                </ThreadItemHeader>
                <ThreadItemPreview>
                  {thread.last_message_body || '—'}
                </ThreadItemPreview>
                {thread.status === 'in_progress' &&
                  thread.assigned_staff_fullname && (
                    <AssignedBadge>
                      Atendendo: {thread.assigned_staff_fullname}
                    </AssignedBadge>
                  )}
              </ThreadItem>
            ))}
          </ThreadListPane>

          <MessagePane>
            {!selectedThread ? (
              <EmptyMessagePane>
                Selecione uma conversa pra visualizar as mensagens.
              </EmptyMessagePane>
            ) : (
              <>
                <MessagePaneHeader>
                  <span>{selectedThread.student_fullname}</span>
                  {selectedThread.status !== 'closed' && (
                    <CloseThreadButton
                      type="button"
                      disabled={closing}
                      onClick={handleCloseThread}
                    >
                      Encerrar atendimento
                    </CloseThreadButton>
                  )}
                </MessagePaneHeader>

                <MessagesScroll>
                  {messages.map(message => {
                    const own = message.sender_user_id !== selectedStudentId;
                    // Mais de um funcionário pode responder o mesmo
                    // aluno -- mostra o nome de quem mandou pra deixar
                    // claro qual colega já respondeu, exceto na própria
                    // mensagem (óbvio de quem é).
                    const showSenderName =
                      own && message.sender_user_id !== user?.id;

                    return (
                      <MessageBubble key={message.id} own={own}>
                        {showSenderName && (
                          <SenderName>{message.sender_fullname}</SenderName>
                        )}
                        {message.body}
                        <MessageTime>
                          há{' '}
                          {formatDistanceToNow(new Date(message.created_at), {
                            locale: ptBR,
                          })}
                        </MessageTime>
                      </MessageBubble>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </MessagesScroll>

                {selectedThread.status === 'closed' ? (
                  <ClosedNotice>
                    Atendimento encerrado. Reaberto automaticamente se o aluno
                    enviar uma nova mensagem.
                  </ClosedNotice>
                ) : (
                  <Composer onSubmit={handleSend}>
                    <ComposerInput
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      placeholder="Escreva uma mensagem..."
                    />
                    <SendButton
                      type="submit"
                      disabled={!draft.trim() || sending}
                    >
                      <Icon name="send" size={20} color={colors.onPrimary} />
                    </SendButton>
                  </Composer>
                )}
              </>
            )}
          </MessagePane>
        </Layout>
      </Container>
    </Painel>
  );
};

export { ChatInbox };
